import { VoidCallback } from '@deqo/foundation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { srand, toSearchable, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { Host } from '../../../Host';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { waitForOperation } from '../../../api/endpoints/operations';
import { NewTemplateTagData } from '../../../api/endpoints/templates';
import { ClipDataBuilder, ClipboardService } from '../../../platform/Clipboard';
import { AppNavigator } from '../../AppNavigator';
import { useResolve } from '../../Container';
import { useAccountGuardCallback } from '../../account/AccountHooks';
import { CreateBoardParams, useOnCreateBoard } from '../../actions/BoardActions';
import { busyPopup } from '../../popups/BusyPopup';
import { PopupManager } from '../../popups/PopupManager';
import { TemplateWithStarData } from './gallery/TemplateListView';
import { boardSelectPopup } from './popups/BoardSelectPopup';
import { TemplateCreatePopupAction, templateCreatePopup } from './popups/TemplateCreatePopup';
import { templateDeletePopup } from './popups/TemplateDeletePopup';

const useLegacyTemplatesAsNext = () => {

    const api = useResolve(ApiClient);
    const { data: legacyData } = useQuery({
        queryKey: ['templates.listOld'],
        queryFn: async () => api.exec(v2.templates.list()),
    });

    return useMemo(() => {
        if (!legacyData) return { templates: [], tags: [] };

        const colors = {
            'Ice Breakers': '#6078CB',
            'Retrospectives': '#7E48F0',
            'Futurespectives': '#F38C6C',
            'Planning & Estimation': '#F46464',
            'Product Boards': '#CB6093',
            'Decision Making': '#488BF0',
            'Ways Of Working': '#F07CAD',
            'Check Out': '#6CB8E3',
            'Tutorials & Guides': '#F6B263',
        };

        const tags: NewTemplateTagData[] = legacyData.map(group => ({
            id: group.id,
            name: group.label,
            label: group.label,
            color: colors[group.label] || '#' + Math.floor(srand(group.label) * 16777215).toString(16).padStart(6, '0'),
            sort: Object.keys(colors).indexOf(group.label),
        }));

        const tagLookup = Object.fromEntries(tags.map(tag => [tag.id, tag]));

        const templates: TemplateWithStarData[] = legacyData.flatMap(group =>
            group.items.filter(x => x.label !== 'Planning Poker' && x.label !== 'T-shirt sizing').map(item => ({
                id: item.id,
                accountId: undefined, // Not available in old structure
                alias: uuid(), // Using id as alias
                label: item.label,
                description: item.description || (!item.special ? 'A custom template.' : ''),
                time: null,
                people: null,
                complexity: null,
                thumbUrl: item.thumbUrl,
                thumbEtag: '', // Not available in old structure
                version: 1, // Assuming version 1 for legacy templates
                publishedAt: new Date(), // Using current date as not available in old structure
                createdAt: new Date(), // Using current date as not available in old structure
                tags: [tagLookup[group.id]],
                metadata: {},
                starred: false,
                team: !item.special,
            }))
        );

        return { templates, tags };

    }, [legacyData]);
};

export const useOnTemplateCreate = () => {

    const api = useResolve(ApiClient);
    const popups = useResolve(PopupManager);
    const qc = useQueryClient();

    return useAccountGuardCallback(async () => {

        for (let cb = 0; cb < 10; cb++) {

            const { data: board } = await popups.invoke(boardSelectPopup());
            if (!board) return;

            const { data: result } = await popups.invoke(templateCreatePopup(board));
            if (Array.isArray(result)) {
                const [action, arg] = result as TemplateCreatePopupAction;
                if (action === 'complete' && arg.executionId) {
                    await popups.invoke(busyPopup(
                        async () => {
                            await waitForOperation(api, arg.executionId);
                            await qc.invalidateQueries(['templates.list']);
                        }
                    ));
                }
                else if (action === 'restart') {
                    continue;
                }
            }

            break;
        }

    }, []);

};

type OnTemplateDeleteCallbacks = {
    onInvoke?: VoidCallback,
    onComplete?: VoidCallback,
};

export const useOnTemplateDelete = (templateId: string, { onInvoke, onComplete }: OnTemplateDeleteCallbacks = {}) => {

    const popups = useResolve(PopupManager);
    return useAccountGuardCallback(async () => {

        onInvoke?.();

        templateDeletePopup(templateId);

        const { actioned, data } = await popups.invoke(templateDeletePopup(templateId));
        if (actioned && data) {
            onComplete?.();
        }

    }, [templateId, onInvoke, onComplete]);

};

export const useOnTemplateCopyLink = (templateId: string, onCopy?: VoidCallback) => {

    const clipboard = useResolve(ClipboardService);

    return useCallback(() => {
        const clip = new ClipDataBuilder();
        clip.setText(`${Host.baseUrl}/new/${templateId}`);
        clipboard.write(clip.build());
        onCopy?.();
    }, [templateId, onCopy]);

};

export const useOnTemplatePreview = (templateId: string) => {

    const nav = useResolve(AppNavigator);

    return useCallback(() => {
        nav.go(`/TP/${templateId}`);
    }, [templateId]);
};

export type CreateBoardFromTemplateParams = Omit<CreateBoardParams, 'source'> & { templateId: string };

export const useOnCreateBoardFromTemplate = () => {

    const onCreateBoard = useOnCreateBoard();

    return useCallback(async ({ templateId, ...rest }: CreateBoardFromTemplateParams) => {
        await onCreateBoard({
            source: ['template', templateId],
            ...rest,
        });
    }, [onCreateBoard]);
};

export const useTemplateData = (version?: string) => {
    if (version === 'legacy') return useLegacyTemplatesAsNext();

    const api = useResolve(ApiClient);

    const tagSorter = (a: NewTemplateTagData, b: NewTemplateTagData) => {
        return (a.sort !== b.sort)
            ? a.sort - b.sort
            : a.label.localeCompare(b.label);
    };

    const { data: templates } = useQuery({
        queryKey: ['templates.list'],
        queryFn: async () => api.exec(v2.templates.list2()),
    });

    const { data: stars } = useQuery({
        queryKey: ['templates.stars.list'],
        queryFn: async () => api.exec(v2.templates.stars.list()),
    });

    return useMemo(() => {

        if (!templates) return {};
        if (!stars) return {};

        const starLookup = new Set(stars);
        const tagLookup = Object.fromEntries(templates.flatMap(x => x.tags).map(x => [x.id, x]));
        const tags = Object.values(tagLookup).sort(tagSorter);

        // Upgrade the data with normalized tags and starred prop
        const upgraded = templates.map(x => ({
            ...x,
            searchableLabel: toSearchable(x.label),
            team: !!x.accountId,
            starred: starLookup.has(x.id),
            tags: x.tags.map(t => tagLookup[t.id]).sort(tagSorter),
        }));

        return { templates: upgraded as TemplateWithStarData[], tags };

    }, [templates, stars]);

};

export const useTemplateStarred = (template: TemplateWithStarData) => {
    const api = useResolve(ApiClient);
    const queries = useQueryClient();
    const [current, setCurrent] = useState(template.starred);

    const create = useMutation({
        mutationFn: async (params: { templateId: string }) => api.exec(v2.templates.stars.create(params.templateId)),
        onSuccess() {
            queries.invalidateQueries(['templates.stars.list']);
            setCurrent(true);
        }
    });

    const destroy = useMutation({
        mutationFn: async (params: { templateId: string }) => api.exec(v2.templates.stars.delete(params.templateId)),
        onSuccess() {
            queries.invalidateQueries(['templates.stars.list']);
            setCurrent(false);
        }
    });

    const toggle = useCallback(() => {
        if (current) destroy.mutate({ templateId: template.id });
        else create.mutate({ templateId: template.id });
        setCurrent(!current);
    }, [current, create, destroy]);

    return [current, toggle] as const;
};
